import { HTTP, flatMap, map, debounce } from '../../lib'
import { update } from '../../lib/vuex'
import Status from '../../lib/types/Status'
import httpClient from '../../api/httpClient'
import { makeDecimalValue, makeValueWithoutDecimal, oneTimeEarningsMap } from '../../utils/helper'
import { serializeTrafficSourceCosts, normalizeTrafficSourceCosts } from '../../utils/trafficSourceCostsUtils'
import { errorHandler } from '../../utils/helper'

const updateDefaults = (commit, offer) => {
  commit('update', { path: 'name', value: offer.name })
  commit('update', { path: 'type', value: offer.type })
  commit('update', { path: 'showForAdvertiser', value: !!offer.show_for_advertiser })
  commit('update', { path: 'priority', value: offer.priority })
  commit('update', { path: 'active', value: !!offer.active })
  commit('update', { path: 'storeVisibility', value: offer.store_visibility })
  commit('update', { path: 'offerCampaignTitle', value: offer.offer_campaign_title })
  commit('update', { path: 'offerCampaignText', value: offer.offer_campaign_text })
  commit('update', {
    path: 'todo',
    value: offer.type !== 'standard' && offer.todo ? offer.todo : [''],
  })
  commit('update', { path: 'startDate', value: offer.from })
  commit('update', { path: 'endDate', value: offer.to })
  commit('update', { path: 'clickLimit', value: offer.max_clicks })
  commit('update', { path: 'eachUserLimit', value: offer.each_user_limit })
  commit('update', { path: 'budgetLimit', value: offer.max_money })
  commit('update', { path: 'imgSrc', value: offer.img_src })
  commit('update', { path: 'image', value: offer.image })
  commit('update', { path: 'oneTimePayment', value: makeDecimalValue(offer.one_time_payment) })
  commit('update', { path: 'hasProductSeeding', value: !!offer.has_product_seeding })
  commit('update', { path: 'hasOnetimePayment', value: !!offer.has_onetime_payment })

  const selectedRevenueTiers = offer.revenue_tier.map((tier) => ({ ...tier.client, selectedTier: tier }))
  const clientsWithTiers = offer.clients.concat(selectedRevenueTiers)

  commit('update', {
    path: 'clients.selected',
    value: clientsWithTiers,
  })

  commit('update', {
    path: 'storeGroups.selected',
    value: offer.store_groups,
  })

  commit('update', {
    path: 'storeGroups.value',
    value: '',
  })

  commit('update', {
    path: 'users.selected',
    value: offer.users.map((user) => ({
      ...user,
      name: `${user.id}${user.email ? ` - ${user.email}` : ''}`,
    })),
  })

  commit('update', {
    path: 'userTags.selected',
    value: offer.user_tags.map((user_tag) => ({
      ...user_tag,
    })),
  })

  commit('update', {
    path: 'selectedStore',
    value: {
      id: offer.store_id,
      name: offer.store ? offer.store.feed_name : 'Undefined Store',
    },
  })

  commit('update', {
    path: 'emailUsers.selected',
    value: offer.email_users.map((email_user) => ({
      ...email_user,
      name: email_user.email,
    })),
  })

  commit('update', {
    path: 'socialMediaUsers.selected',
    value: offer.social_media_users.map((social_media_user) => ({
      ...social_media_user,
      name: social_media_user.identifier,
    })),
  })

  const hasTrafficSourceInvoice = offer.traffic_sources_costs.some(
    (item) => item.invoice_cpa || item.invoice_cpa == 0 || item.invoice_cpc || item.invoice_cpc == 0,
  )
  commit('update', { path: 'isActiveInvoice', value: hasTrafficSourceInvoice })
  commit('update', { path: 'trafficSourceCosts', value: normalizeTrafficSourceCosts(offer.traffic_sources_costs) })
}

const serialize = (state) => {
  const name = state.ui.name
  const type = state.ui.type
  const active = state.ui.active ? 1 : 0
  const showForAdvertiser = state.ui.showForAdvertiser ? 1 : 0
  const storeVisibility = state.ui.storeVisibility
  const startDate = state.ui.startDate === '' ? null : state.ui.startDate
  const endDate = state.ui.endDate === '' ? null : state.ui.endDate
  const clickLimit = state.ui.clickLimit === '' ? null : state.ui.clickLimit
  const eachUserLimit = state.ui.eachUserLimit
  const hasProductSeeding = state.ui.hasProductSeeding
  const hasOnetimePayment = state.ui.hasOnetimePayment
  const budgetLimit = state.ui.budgetLimit === '' ? null : state.ui.budgetLimit
  const oneTimePayment = state.ui.oneTimePayment === '' ? null : makeValueWithoutDecimal(state.ui.oneTimePayment)
  const storeGroups = state.ui.storeGroups.selected
  const clientIds = state.ui.clients.selected.filter((client) => !client.selectedTier).map((client) => client.id)
  const clients = clientIds.filter((id, pos) => clientIds.indexOf(id) === pos).map((id) => ({ id }))
  const revenueTiers = state.ui.clients.selected.filter((client) => client.selectedTier).map((client) => ({ id: client.selectedTier.id }))
  const users = state.ui.users.selected.map((user) => ({ id: user.id }))
  const userTags = state.ui.userTags.selected.map((userTag) => ({ id: userTag.id }))
  const priority = state.ui.priority
  const offerCampaignTitle = state.ui.offerCampaignTitle
  const offerCampaignText = state.ui.offerCampaignText
  let todo = null
  if (state.ui.type !== 'standard') {
    todo = state.ui.todo.filter((item) => item.length)
  }
  const storeId = state.ui.selectedStore.id

  const emailUsers = state.ui.emailUsers.selected.map((emailUser) => emailUser.name)
  const socialMediaUsers = state.ui.socialMediaUsers.selected.map((socialMediaUser) => socialMediaUser.name)

  return {
    name,
    type,
    store_id: storeId,
    active,
    show_for_advertiser: showForAdvertiser,
    store_visibility: storeVisibility,
    store_groups: storeGroups,
    clients,
    revenue_tiers: revenueTiers,
    users,
    user_tags: userTags,
    max_clicks: clickLimit,
    each_user_limit: eachUserLimit,
    has_product_seeding: hasProductSeeding,
    has_onetime_payment: hasOnetimePayment,
    max_money: budgetLimit,
    one_time_payment: oneTimePayment,
    from: startDate,
    to: endDate,
    priority,
    offer_campaign_title: offerCampaignTitle,
    offer_campaign_text: offerCampaignText,
    todo,
    email_users: emailUsers,
    social_media_users: socialMediaUsers,
    traffic_sources_costs: serializeTrafficSourceCosts(state.ui.trafficSourceCosts),
  }
}

const findUsers = (commit, state, query) =>
  httpClient
    .get(`/users/find`, {
      params: { query },
    })
    .then((response) => {
      query === state.ui.users.value &&
        commit('update', {
          path: 'users.suggestions',
          value: map(response.data.data, (user) => ({
            ...user,
            name: `${user.id}${user.email && ` - ${user.email}`}${user.username && ` - @${user.username}`}`,
            one_time_earnings: oneTimeEarningsMap(user.one_time_earnings),
          })),
        })
    })
    .catch((error) => console.error(error))

findUsers.delayed = debounce(findUsers, 250)

const findClients = (commit, state, query) =>
  httpClient
    .get('/clients', {
      params: { query },
    })
    .then(
      (response) =>
        query === state.ui.clients.value &&
        commit('update', {
          path: 'clients.suggestions',
          value: flatMap(response.data.data, (client) => [
            client,
            ...map(client.revenue_tiers, (tier) => ({
              ...client,
              selectedTier: tier,
            })),
          ]),
        }),
    )
    .catch((err) => console.error(err))

findClients.delayed = debounce(findClients, 250)

const findUserTags = (commit, state, query) =>
  httpClient
    .get(`/admin/user-tags`, {
      params: { query },
    })
    .then((response) => {
      query === state.ui.userTags.value &&
        commit('update', {
          path: 'userTags.suggestions',
          value: map(response.data, (user_tag) => ({
            ...user_tag,
          })),
        })
    })
    .catch((error) => console.error(error))

findUserTags.delayed = debounce(findUserTags, 250)

const emptyOffer = {
  currentStep: 0,
  name: '',
  type: 'standard',
  store_id: '',
  active: true,
  isActiveInvoice: false,
  show_for_advertiser: false,
  startDate: '',
  endDate: '',
  clickLimit: '',
  eachUserLimit: 0,
  hasProductSeeding: false,
  hasOnetimePayment: false,
  budgetLimit: '',
  oneTimePayment: '',
  priority: 1,
  offer_campaign_title: '',
  offer_campaign_text: '',
  todo: [''],

  stores: {
    suggestions: [],
    value: '',
    selected: [],
  },

  storeGroups: {
    suggestions: [],
    value: '',
    selected: [],
  },
  storesGroupFind: {
    suggestions: [],
    value: '',
    selected: null,
  },
  clients: {
    suggestions: [],
    value: '',
    selected: [],
  },

  users: {
    suggestions: [],
    value: '',
    selected: [],
  },

  userTags: {
    suggestions: [],
    value: '',
    selected: [],
  },

  selectedStore: {},

  status: Status.Idle(),

  emailUsers: {
    suggestions: [],
    value: '',
    selected: [],
  },

  socialMediaUsers: {
    suggestions: [],
    value: '',
    selected: [],
  },

  imgSrc: '',
  image: null,
  imgFile: null,
  trafficSourceCosts: [],
}

export default {
  state: {
    ui: emptyOffer,
    errorMessageList: [],
    selectedOfferStatus: HTTP.Request.Pending(),
  },

  mutations: {
    addItem(state, { key, value }) {
      if (
        state.ui[key].selected.some((item) => {
          if (value.selectedTier) return value.selectedTier.id === (item.selectedTier || {}).id

          if (item.selectedTier) return false

          return item.id != null ? item.id === value.id : item.name === value.name
        }) === false &&
        value.name !== ''
      )
        state.ui[key].selected.push(value)
    },

    removeItem(state, { key, value }) {
      state.ui[key].selected = state.ui[key].selected.filter((item) => {
        if (value.selectedTier) return (item.selectedTier || {}).id !== value.selectedTier.id

        if (item.selectedTier) return true

        return item.id != null ? item.id !== value.id : item.name !== value.name
      })
    },

    addTodoItem(state) {
      state.ui.todo.push('')
    },

    removeTodoItem(state, index) {
      state.ui.todo.splice(index, 1)
    },

    updateTodoItem(state, { id, value }) {
      state.ui.todo.splice(id, 1, value)
    },

    update(state, { path, value }) {
      state.ui = update(state.ui, path, value)
    },

    updateSelectedOfferStatus(state, value) {
      state.selectedOfferStatus = value
    },

    changeStep(state, step) {
      state.ui.currentStep = step
    },

    resetState(state) {
      state.ui = emptyOffer
      // Adding this lines because emptyOffer does not clear the arrays at all.
      state.ui.clients.selected = []
      state.ui.users.selected = []
      state.ui.storeGroups.selected = []
      state.ui.userTags.selected = []
      state.ui.emailUsers.selected = []
      state.ui.socialMediaUsers.selected = []
      state.ui.imgSrc = ''
      state.ui.todo = ['']
      state.ui.imgFile = null
      state.errorMessageList = []
      state.ui.trafficSourceCosts = []
    },

    setImgFile(state, file) {
      state.ui.imgFile = file
    },

    setImage(state, url) {
      if (url) {
        const rand = Math.random().toString(36).substring(7)
        state.ui.imgSrc = url + '?rand=' + rand
      }
    },

    setErrorMessageList(state, value) {
      state.errorMessageList = value
    },
  },

  getters: {
    name: ({ ui: { name } }) => name,
    type: ({ ui: { type } }) => type,
    selectedStore: ({ ui: { selectedStore } }) => selectedStore,
    active: ({ ui: { active } }) => active,
    isActiveInvoice: ({ ui: { isActiveInvoice } }) => isActiveInvoice,
    showForAdvertiser: ({ ui: { showForAdvertiser } }) => showForAdvertiser,
    storeVisibility: ({ ui: { storeVisibility } }) => storeVisibility,
    startDate: ({ ui: { startDate } }) => startDate,
    clickLimit: ({ ui: { clickLimit } }) => clickLimit,
    eachUserLimit: ({ ui: { eachUserLimit } }) => eachUserLimit,
    hasProductSeeding: ({ ui: { hasProductSeeding } }) => hasProductSeeding,
    hasOnetimePayment: ({ ui: { hasOnetimePayment } }) => hasOnetimePayment,
    endDate: ({ ui: { endDate } }) => endDate,
    budgetLimit: ({ ui: { budgetLimit } }) => budgetLimit,
    oneTimePayment: ({ ui: { oneTimePayment } }) => oneTimePayment,
    stores: ({ ui: { stores } }) => stores,
    storeGroups: ({ ui: { storeGroups } }) => storeGroups,
    clients: ({ ui: { clients } }) => clients,
    users: ({ ui: { users } }) => users,
    userTags: ({ ui: { userTags } }) => userTags,
    currentStep: ({ ui: { currentStep } }) => currentStep,
    storesGroupFind: ({ ui: { storesGroupFind } }) => storesGroupFind,
    status: ({ ui: { status } }) => status,
    priority: ({ ui: { priority } }) => priority,
    offerCampaignTitle: ({ ui: { offerCampaignTitle } }) => offerCampaignTitle,
    offerCampaignText: ({ ui: { offerCampaignText } }) => offerCampaignText,
    selectedOfferStatus: ({ selectedOfferStatus }) => selectedOfferStatus,
    emailUsers: ({ ui: { emailUsers } }) => emailUsers,
    socialMediaUsers: ({ ui: { socialMediaUsers } }) => socialMediaUsers,
    imgSrc: ({ ui: { imgSrc } }) => imgSrc,
    image: ({ ui: { image } }) => image,
    todo: ({ ui: { todo } }) => todo,
    errorMessage: ({ errorMessageList }) => (errorMessageList.length ? errorMessageList : 'Something went wrong!'),
    trafficSourceCosts: ({ ui: { trafficSourceCosts } }) => trafficSourceCosts,
  },

  actions: {
    update({ commit }, { path, value }) {
      commit('update', { path, value })
    },

    addItem({ commit }, { key, value }) {
      commit('addItem', { key, value })
    },

    removeItem({ commit }, { key, value }) {
      commit('removeItem', { key, value })
    },

    init({ commit, state, rootGetters }, id) {
      commit('resetState')
      commit('updateSelectedOfferStatus', HTTP.Request.Pending())
      commit('setImgFile', null)

      if (id) {
        let item = null
        if (rootGetters['offers/list/offers']) {
          item = rootGetters['offers/list/offers'].find((offer) => offer.id.toString() === id)
        }

        if (item) {
          updateDefaults(commit, item)
          // commit("setImg", response.data.img_src);
          commit('updateSelectedOfferStatus', HTTP.Request.Done())
        } else {
          httpClient
            .get(`/offers/${id}`)
            .then((response) => {
              updateDefaults(commit, response.data)
              commit('updateSelectedOfferStatus', HTTP.Request.Done())
            })
            .catch((err) => (console.error(err), commit('updateSelectedOfferStatus', err)))
        }
      } else {
        commit('resetState')
        commit('updateSelectedOfferStatus', HTTP.Request.Done())
      }
      httpClient
        .get(`/storeGroups`)
        .then((response) => {
          commit('update', {
            path: 'storeGroups.suggestions',
            value: response.data,
          })
          commit('update', {
            path: 'storesGroupFind.suggestions',
            value: response.data,
          })
        })
        .catch((err) => console.error(err))

      findUsers(commit, state, '')

      findClients(commit, state, '')

      findUserTags(commit, state, '')
    },

    find({ commit, state }, key) {
      if (key === 'stores') {
        const selectedStoreGroup = state.ui.storesGroupFind.value

        httpClient.get(`/storeGroups/${selectedStoreGroup}/stores`).then((response) => {
          commit('update', {
            path: 'stores.suggestions',
            value: response.data.map(({ feed_name, id }) => ({ name: feed_name, id: id })),
          })
        })

        // HTTP.post(
        //   "https://dbc0110679e544c7901d103e78e566f6.eu-west-1.aws.found.io:9243/" +
        //     selectedStoreGroup +
        //     "/_search?size=0",
        //   {
        //     body: {
        //       aggs: {
        //         manufacturers: {
        //           terms: {
        //             field: "retailer.keyword",
        //             size: 1000,
        //             order: { avg_cpc: "desc" },
        //           },
        //           aggs: {
        //             avg_cpc: { avg: { field: "cpc" } },
        //             categories: { terms: { field: "categories", size: 1 } },
        //           },
        //         },
        //       },
        //     },
        //   },
        // ).then(data =>
        //   commit("update", {
        //     path: "stores.suggestions",
        //     value: data.body.aggregations.manufacturers.buckets.map(
        //       ({ key }) => ({ name: key }),
        //     ),
        //   }),
        // );
      }
    },

    findUsers({ commit, state }, query) {
      findUsers.delayed(commit, state, query)
    },

    findUserTags({ commit, state }, query) {
      findUserTags.delayed(commit, state, query)
    },

    findClients({ commit, state }, query) {
      findClients.delayed(commit, state, query)
    },

    changeStep({ commit }, step) {
      commit('changeStep', step)
    },

    updateImgFile({ commit }, value) {
      commit('setImgFile', value)
    },

    save({ commit, state }) {
      commit('setErrorMessageList', [])
      commit('update', { path: 'status', value: Status.Processing() })
      httpClient
        .post('/offers', serialize(state))
        .then((response) => {
          const { type } = response.data
          if (type !== 'standard') {
            if (state.ui.imgFile) {
              uploadImg(
                state.ui.imgFile,
                response.data.id,
                () => {
                  // commit('setImg', res.data.url)
                  commit('update', { path: 'status', value: Status.Saved() })
                },
                (error) => {
                  const message = 'image update'
                  errorHandler({ message, state, commit, error })
                  commit('update', { path: 'status', value: Status.Failed() })
                },
              )
            } else {
              commit('update', { path: 'status', value: Status.Saved() })
            }
          } else {
            commit('update', { path: 'status', value: Status.Saved() })
          }
        })
        .catch((error) => {
          const message = 'offer create'
          errorHandler({ message, state, commit, error })
          commit('update', { path: 'status', value: Status.Failed() })
        })
    },

    edit({ commit, state }, id) {
      commit('setErrorMessageList', [])
      commit('update', { path: 'status', value: Status.Processing() })
      httpClient
        .put(`/offers/v2/${id}`, serialize(state))
        .then((response) => {
          const { type } = response.data
          if (type !== 'standard') {
            if (state.ui.imgFile) {
              uploadImg(
                type !== 'standard' ? state.ui.imgFile : '',
                response.data.id,
                () => {
                  // commit('setImg', res.data.url)
                  commit('update', { path: 'status', value: Status.Saved() })
                },
                (error) => {
                  const message = 'image update'
                  errorHandler({ message, state, commit, error })
                  commit('setStatus', Status.Failed())
                },
              )
            } else {
              commit('update', { path: 'status', value: Status.Updated() })
            }
          } else {
            commit('update', { path: 'status', value: Status.Updated() })
          }
        })
        .catch((error) => {
          const message = 'offer edit'
          errorHandler({ commit, error, state, message })
          commit('update', { path: 'status', value: Status.Failed() })
        })
    },

    addTodoItem({ commit }) {
      commit('addTodoItem')
    },

    removeTodoItem({ commit }, data) {
      commit('removeTodoItem', data)
    },

    updateTodoItem({ commit }, data) {
      commit('updateTodoItem', data)
    },
  },
}

const uploadImg = (img, offerId, onComplete, onError) => {
  const formData = new FormData()
  formData.append('default', img)
  httpClient
    .post(`/v2/offers/${offerId}/uploads`, formData)
    .then((response) => {
      onComplete(response)
      return response
    })
    .catch((error) => onError(error.response.data))
}
